import { css } from '@emotion/react';
import React, { useState, useContext, useRef } from 'react';
import moment, { Moment } from 'moment-timezone';
import { debounce } from 'underscore';

import t from 'react-translate';
import { useSelector } from 'react-redux';

import { AngularContext } from 'react-app';

import { RootState } from 'redux/schemas';
import { getCurrentUser } from 'redux/selectors/users';
import { InPersonEventSession } from 'redux/schemas/models/in-person-event';
import { PreventNavigationReason } from 'redux/schemas/models/lecture-page';

import { halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { gray2 } from 'styles/global_defaults/colors';
import { textMediumFontSize, textMediumLineHeight } from 'styles/global_defaults/fonts';

import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvDatepicker, { DatePickerType } from 'shared/components/inputs/nv-datepicker';
import NvTimezoneSelector from 'shared/components/nv-timezone-selector';
import NvGooglePlacesAutocomplete from 'components/nv-google-places-autocomplete';
import NvIcon from 'shared/components/nv-icon';
import { Timezone } from 'live_sessions/components/use-timezones';
import usePreventLecturePageNavigation from 'lecture_pages/hooks/use-prevent-lecture-page-navigation';
import NvFroala from 'froala/components/nv-froala';
import { FroalaViewMode, UploadType } from 'froala/helpers/nv-froala-constants';
import { InPersonEventSessionUpdate, useUpdateInPersonEvent } from '../in-person-event-utils';
import { useInPersonEventContext } from '../in-person-event-context';

const placeholderStyle = css`
  font-size: ${textMediumFontSize}px;
  line-height: ${textMediumLineHeight}px;
  color: ${gray2};
  font-weight: normal;
`;

const styles = css`
  border-radius: ${halfSpacing}px;
  gap: ${standardSpacing}px;

  input::placeholder {
    ${placeholderStyle}
  }

  textarea {
    min-height: 140px;
    ${placeholderStyle}
  }

  .date-input {
    max-width: 160px;
  }

  .location-input {
    min-width: 280px;
  }
  .rounded {
    border-radius: ${quarterSpacing}px;
  }
  /** To override froala's default border radius Which causing the custom input border-radius to break*/
  .froala-description .fr-box,
  .froala-description .fr-wrapper,
  .froala-description .fr-element {
    border-radius: ${quarterSpacing}px;
    .fr-placeholder {
      background-color: unset !important;
    }
  }

`;

const InPersonEventForm = () => {
  const { sessionId } = useInPersonEventContext();
  const { injectServices } = useContext(AngularContext);
  const [TimezonesService] = injectServices(['TimezonesService']);

  const currentUser = useSelector(getCurrentUser);
  const inPersonEventSession = useSelector((state: RootState) => (
    state.models.inPersonEventSessions[sessionId]) as InPersonEventSession | undefined);
  const [title, setTitle] = useState<string>(inPersonEventSession?.title ?? '');
  const [description, setDescription] = useState<string>(inPersonEventSession?.description ?? '');
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
  const [isTitleFocused, setIsTitleFocused] = useState(false);

  const hasTitleError = !title.trim();
  const hasStartTimeError = !inPersonEventSession?.startTime;
  const hasEndTimeError = !inPersonEventSession?.endTime;
  const hasLocationError = !inPersonEventSession?.location?.trim();
  const hasValidationErrors = hasTitleError || hasStartTimeError || hasEndTimeError || hasLocationError;

  usePreventLecturePageNavigation(() => hasValidationErrors, PreventNavigationReason.UNSAVED_CHANGES);

  const allTimezones: Timezone[] = TimezonesService.timezones;
  const selectedTimezone = allTimezones.find((tz) => tz.longName === inPersonEventSession?.timeZone);

  const updateInPersonEvent = useUpdateInPersonEvent();

  const inPersonEventSessionRef = useRef(inPersonEventSession);
  inPersonEventSessionRef.current = inPersonEventSession;

  const saveSession = (updates: InPersonEventSessionUpdate) => {
    const session = inPersonEventSessionRef.current;
    const merged = { ...session, ...updates };
    if (!merged.timeZone) {
      merged.timeZone = currentUser.timeZone || moment.tz.guess();
    }
    updateInPersonEvent(merged, 'eventSessions');
  };

  const saveSessionRef = useRef(saveSession);
  saveSessionRef.current = saveSession;

  const debouncedSetStartDate = useRef(debounce((date: Moment) => {
    if (!date || !date.isValid()) {
      saveSessionRef.current({ startTime: '' });
      return;
    }
    const session = inPersonEventSessionRef.current;

    if (date.isSame(moment(session.startTime))) return;

    const updates: InPersonEventSessionUpdate = { startTime: date.toISOString() };
    if (session?.endTime && date.isAfter(moment(session.endTime))) {
      updates.endTime = '';
    }
    saveSessionRef.current(updates);
  }, 300)).current;

  const debouncedSetEndDate = useRef(debounce((date: Moment) => {
    if (!date || !date.isValid()) {
      saveSessionRef.current({ endTime: '' });
      return;
    }
    const session = inPersonEventSessionRef.current;

    if (date.isSame(moment(session.endTime))) return;

    let dateTime = date.toISOString();

    if (date.isBefore(moment(session.startTime))) {
      dateTime = session.startTime;
    }

    saveSessionRef.current({ endTime: dateTime });
  }, 300)).current;

  const inputClassName = 'bg-white border rounded';
  const getBorderClass = (hasError: boolean) => (hasError ? 'border-danger' : 'border-gray-3');

  return (
    <div
      className='d-flex flex-column w-100 p-5 border border-gray-5'
      css={styles}
    >
      <NvTextInput
        placeholder={isTitleFocused ? t.FORM.TYPE_HERE() : t.FROALA.FONT.TITLE()}
        value={title}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
        onFocus={() => setIsTitleFocused(true)}
        onBlur={() => {
          setIsTitleFocused(false);
          if (title !== inPersonEventSession?.title) {
            saveSession({ title });
          }
        }}
        inputClassName={`${inputClassName} text-large-regular bold ${getBorderClass(hasTitleError)}`}
        withLabel
        label={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.EVENT_TITLE()}
        showLabelOnFocus
        maxLength={255}
        min={1}
      />
      <div>
        <label
          className='text-gray-1 mb-0'
          style={{ visibility: (isDescriptionFocused || description) ? 'visible' : 'hidden' }}
        >
          {t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.EVENT_DESCRIPTION()}
        </label>

        <NvFroala
          value={description}
          onChange={setDescription}
          preset={FroalaViewMode.NORMAL}
          immediateReactModelUpdate
          onFocus={() => setIsDescriptionFocused(true)}
          onBlur={() => {
            setIsDescriptionFocused(false);
            if (description !== inPersonEventSession?.description) {
              saveSession({ description });
            }
          }}
          className={`froala-description ${inputClassName} text-large-body ${getBorderClass(false)}`}
          placeholder={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.EVENT_DESCRIPTION()}
          uploadType={UploadType.NONE}
        />
      </div>

      <div className='d-flex justify-content-between'>
        <div className='d-flex mr-4'>
          <NvIcon icon='dueday' size='medium' className='text-warning mr-2 mt-2' />
          <div className='d-flex flex-column'>
            <div className='d-flex mb-4'>
              <div>
                <NvDatepicker
                  type={DatePickerType.DATETIME}
                  placeholder={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.FROM()}
                  value={inPersonEventSession?.startTime ? moment(inPersonEventSession.startTime) : undefined}
                  onChange={(date) => debouncedSetStartDate(date)}
                  className='mr-2'
                  inputClassName={`date-input ${inputClassName} ${getBorderClass(hasStartTimeError)}`}
                  min={moment().startOf('day')}
                />
              </div>
              <div>
                <NvDatepicker
                  type={DatePickerType.DATETIME}
                  placeholder={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.TO()}
                  value={inPersonEventSession?.endTime ? moment(inPersonEventSession.endTime) : undefined}
                  onChange={(date) => debouncedSetEndDate(date)}
                  inputClassName={`date-input ${inputClassName} ${getBorderClass(hasEndTimeError)}`}
                  min={inPersonEventSession?.startTime ? moment(inPersonEventSession.startTime) : moment().startOf('day')}
                />
              </div>
            </div>
            <div>
              <label htmlFor='timezone-selector'>
                {t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.TIME_ZONE()}
              </label>
              <div>
                <NvTimezoneSelector
                  selectedTimezone={selectedTimezone}
                  defaultTimezone={inPersonEventSession?.timeZone ?? currentUser.timeZone}
                  onchange={(timezone) => saveSession({ timeZone: timezone.longName })}
                  className={`${inputClassName} w-100 ${getBorderClass(false)}`}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='d-flex flex-column align-self-start'>
          <div className='d-flex align-items-center'>
            <NvIcon icon='location' size='medium' className='text-warning mr-2' />
            <NvGooglePlacesAutocomplete
              placeholder={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.LOCATION()}
              value={inPersonEventSession?.location ?? ''}
              onBlur={(e) => {
                const newLocation = e.target.value;
                if (newLocation !== inPersonEventSession?.location) {
                  saveSession({ location: newLocation });
                }
              }}
              className={`location-input ${inputClassName} ${getBorderClass(hasLocationError)}`}
              types={['address']}
              allowManualEntry
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InPersonEventForm;
